import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  BookOpen, CheckCircle2, Clock, Download, Eye, FileText, Loader2,
  MoreVertical, PenLine, Plus, Printer, Scale, Search, Send, Shield, Star,
} from "lucide-react";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { Input } from "../ui/input";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from "../ui/dropdown-menu";
import DocumentCard from "./DocumentCard";
import InviaModuloDialog from "./InviaModuloDialog";
import ModuloFormDialog from "./ModuloFormDialog";
import PrintPreviewModal from "./PrintPreviewModal";
import StoricoInviiDialog from "./StoricoInviiDialog";
import { useAuth } from "../../context/AuthContext";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ModuliDaFirmareTab({ token }) {
  const { activeStruttura } = useAuth();
  const [moduli, setModuli] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [inviaOpen, setInviaOpen] = useState(false);
  const [selectedModulo, setSelectedModulo] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [storicoOpen, setStoricoOpen] = useState(false);
  const [formOpen, setFormOpen] = useState(false);
  const [editingModulo, setEditingModulo] = useState(null);

  const fetchModuli = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const { data } = await axios.get(`${API_URL}/api/moduli-firma`, { headers: { Authorization: `Bearer ${token}` } });
      setModuli(data.items || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore caricamento moduli");
    } finally {
      setLoading(false);
    }
  }, [token, activeStruttura?.id]); // eslint-disable-line react-hooks/exhaustive-deps -- refetch al cambio struttura

  useEffect(() => { fetchModuli(); }, [fetchModuli]);

  const filtered = useMemo(() => {
    const q = search.toLowerCase().trim();
    if (!q) return moduli;
    return moduli.filter((m) => m.titolo.toLowerCase().includes(q) || (m.descrizione || "").toLowerCase().includes(q));
  }, [moduli, search]);

  const uploadModello = (moduloId) => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = ".pdf";
    input.onchange = async (e) => {
      const file = e.target.files?.[0];
      if (!file) return;
      const fd = new FormData();
      fd.append("file", file);
      try {
        await axios.post(`${API_URL}/api/moduli-firma/${moduloId}/modello`, fd, {
          headers: { Authorization: `Bearer ${token}`, "Content-Type": "multipart/form-data" },
        });
        toast.success("Modello caricato");
        fetchModuli();
      } catch {
        toast.error("Errore upload");
      }
    };
    input.click();
  };

  const openCreateModulo = () => { setEditingModulo(null); setFormOpen(true); };
  const openEditModulo = (m) => { setEditingModulo(m); setFormOpen(true); };

  const formatDate = (d) => (d ? new Date(d).toLocaleDateString("it-IT") : "—");

  return (
    <>
      <div className="mb-5 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-start gap-3">
          <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-purple-50 text-purple-600 shadow-sm ring-1 ring-purple-100">
            <PenLine className="h-6 w-6" />
          </div>
          <div>
            <h2 className="text-lg font-bold text-slate-900">Moduli da firmare</h2>
            <p className="text-sm text-slate-500">Invia documenti ai familiari per firma digitale o caricamento PDF firmato.</p>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <Input placeholder="Cerca moduli..." value={search} onChange={(e) => setSearch(e.target.value)} className="h-10 w-48 rounded-xl pl-9" />
          </div>
          <Button className="h-10 rounded-xl bg-purple-600 hover:bg-purple-700" onClick={openCreateModulo}>
            <Plus className="mr-2 h-4 w-4" />Nuovo modulo
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="h-8 w-8 animate-spin text-[#00A86B]" /></div>
      ) : (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {filtered.map((m) => (
            <DocumentCard
              key={m.id}
              icon={Shield}
              iconWrap="bg-purple-50 text-purple-600 ring-purple-100"
              title={m.titolo}
              description={m.descrizione}
              badge={
                m.modello_caricato ? (
                  <Badge variant="outline" className="border-emerald-200 bg-emerald-50 text-[10px] text-emerald-700">Modello caricato</Badge>
                ) : (
                  <Badge variant="outline" className="text-[10px]">Modello mancante</Badge>
                )
              }
              menu={
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="icon" className="h-7 w-7"><MoreVertical className="h-4 w-4" /></Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem onClick={() => openEditModulo(m)}>Modifica</DropdownMenuItem>
                    <DropdownMenuItem onClick={() => uploadModello(m.id)}>Carica / sostituisci modello</DropdownMenuItem>
                    <DropdownMenuItem onClick={() => { setSelectedModulo(m); setStoricoOpen(true); }}>Storico invii</DropdownMenuItem>
                    <DropdownMenuItem onClick={() => axios.post(`${API_URL}/api/moduli-firma/${m.id}/duplica`, {}, { headers: { Authorization: `Bearer ${token}` } }).then(() => { toast.success("Duplicato"); fetchModuli(); })}>Duplica</DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem className="text-red-600" onClick={() => window.confirm("Eliminare?") && axios.delete(`${API_URL}/api/moduli-firma/${m.id}`, { headers: { Authorization: `Bearer ${token}` } }).then(() => { toast.success("Eliminato"); fetchModuli(); })}>Elimina</DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              }
              footerLeft={`PDF · Agg. ${formatDate(m.updated_at)}`}
              primaryAction={
                <Button variant="outline" size="sm" className="h-7 border-[#00A86B] px-2 text-[11px] text-[#00A86B]" disabled={!m.modello_caricato} onClick={(e) => { e.stopPropagation(); setSelectedModulo(m); setInviaOpen(true); }}>
                  <Send className="mr-1 h-3 w-3" />Invia
                </Button>
              }
              secondaryAction={
                <Button variant="outline" size="sm" className="h-7 px-2 text-[11px]" disabled={!m.modello_caricato} onClick={(e) => { e.stopPropagation(); setSelectedModulo(m); setPreviewOpen(true); }}>
                  <Printer className="mr-1 h-3 w-3" />Stampa
                </Button>
              }
            >
              <div className="flex gap-3 text-xs text-slate-500">
                <span className="flex items-center gap-1" title="Inviati"><Send className="h-3 w-3 text-purple-500" />{m.stats?.inviati || 0}</span>
                <span className="flex items-center gap-1" title="In attesa"><Clock className="h-3 w-3 text-amber-500" />{m.stats?.in_attesa || 0}</span>
                <span className="flex items-center gap-1" title="Firmati"><CheckCircle2 className="h-3 w-3 text-emerald-500" />{m.stats?.firmati || 0}</span>
              </div>
            </DocumentCard>
          ))}
          <button type="button" onClick={openCreateModulo} className="flex h-[240px] flex-col items-center justify-center rounded-xl border-2 border-dashed border-slate-200 text-slate-400 hover:border-purple-300 hover:text-purple-600">
            <Plus className="mb-2 h-8 w-8" /><span className="text-sm">Nuovo modulo</span>
          </button>
        </div>
      )}

      <div className="mt-6 rounded-xl border border-purple-100 bg-purple-50/60 p-4 text-sm text-slate-600">
        I moduli firmati vengono automaticamente archiviati nel Fascicolo Assistenziale dell&apos;ospite.
      </div>

      <ModuloFormDialog open={formOpen} onOpenChange={setFormOpen} modulo={editingModulo} token={token} onSaved={fetchModuli} />
      <InviaModuloDialog
        open={inviaOpen}
        onOpenChange={(v) => { setInviaOpen(v); if (!v) fetchModuli(); }}
        modulo={selectedModulo}
        token={token}
      />
      <StoricoInviiDialog open={storicoOpen} onOpenChange={setStoricoOpen} modulo={selectedModulo} token={token} />
      {selectedModulo && (
        <PrintPreviewModal
          open={previewOpen}
          onOpenChange={setPreviewOpen}
          doc={{ file_name: selectedModulo.titolo }}
          token={token}
          customUrl={`${API_URL}/api/moduli-firma/${selectedModulo.id}/modello`}
        />
      )}
    </>
  );
}
